import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Eagerly load critical pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy load all other pages
const Features = lazy(() => import("./pages/Features"));
const Cases = lazy(() => import("./pages/Cases"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DocumentDetail = lazy(() => import("./pages/DocumentDetail"));
const SharedViewer = lazy(() => import("./pages/SharedViewer"));
const SharedRoom = lazy(() => import("./pages/SharedRoom"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Technology = lazy(() => import("./pages/Technology"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const UserGuide = lazy(() => import("./pages/UserGuide"));
const FAQ = lazy(() => import("./pages/FAQ"));
const AIDocGen = lazy(() => import("./pages/AIDocGen"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ESignContract = lazy(() => import("./pages/ESignContract"));
const FileUploadPage = lazy(() => import("./pages/FileUploadPage"));
const AIChatWidget = lazy(() => import("@/components/AIChatWidget"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
  </div>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  return <>{children}</>;
};

const App = () => {
  // Global safety net for unhandled async errors
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
        <AuthProvider>
          <LocaleProvider>
            <TooltipProvider>
              <ErrorBoundary>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <ScrollToTop />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/features" element={<Features />} />
                      <Route path="/cases" element={<Cases />} />
                      <Route path="/technology" element={<Technology />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/document/:id" element={<ProtectedRoute><DocumentDetail /></ProtectedRoute>} />
                      <Route path="/view/:token" element={<SharedViewer />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/room/:token" element={<SharedRoom />} />
                      <Route path="/verify" element={<ProtectedRoute><VerifyCertificate /></ProtectedRoute>} />
                      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                      <Route path="/esign" element={<ProtectedRoute><ESignContract /></ProtectedRoute>} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/api-docs" element={<ApiDocs />} />
                      <Route path="/guide" element={<UserGuide />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/ai-doc-gen" element={<AIDocGen />} />
                      <Route path="/upload" element={<FileUploadPage />} />
                      
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
                <Suspense fallback={null}>
                  <AIChatWidget />
                </Suspense>
              </ErrorBoundary>
            </TooltipProvider>
          </LocaleProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;